/**
 * Landing page and blog analytics factories.
 *
 * The landing page and blog surfaces share the PostHog tracking
 * conventions of the other factories but describe their own events,
 * so they are grouped here to keep each analytics module small.
 */

import { usePostHog } from 'posthog-js/react';

// ============================================================================
// Landing & Blog Event Types
// ============================================================================

interface LandingPageAnalytics {
  pageViewed: () => void;
  trialCTAClicked: (properties: { location: 'hero' | 'closing' }) => void;
  demoCTAClicked: (properties: {
    location: 'hero' | 'solution-hero' | 'solution-closing';
  }) => void;
  planSelected: (properties: { planName: string; billingCycle: string }) => void;
  navbarCTAClicked: (properties: { label: string }) => void;
  capabilityClicked: (properties: { title: string; href: string }) => void;
  eventClicked: (properties: { title: string; slug: string }) => void;
  campaignCTAClicked: (properties: {
    code: string;
    location: 'hero' | 'pricing' | 'closing';
  }) => void;
}

interface BlogFeaturesCTAProperties {
  postSlug: string;
  postTitle?: string;
  ctaType: string;
  ctaLabel: string;
  isAuthenticated?: boolean;
}

interface BlogPostViewProperties {
  postSlug: string;
  postTitle: string;
}


// ============================================================================
// Landing & Blog Factories
// ============================================================================

export function createLandingPageAnalytics(posthog: ReturnType<typeof usePostHog>): LandingPageAnalytics {
  const track = (eventName: string, properties?: Record<string, unknown>) => {
    if (!posthog) {
      console.warn('[Analytics] PostHog not initialized');
      return;
    }

    const enrichedProperties = {
      ...properties,
      timestamp: properties?.timestamp || new Date().toISOString(),
      product: 'landing_page',
    };

    posthog.capture(eventName, enrichedProperties);
  };

  return {
    pageViewed: () => track('landing_page_viewed'),
    trialCTAClicked: (properties) => track('landing_trial_cta_clicked', properties),
    demoCTAClicked: (properties) => track('landing_demo_cta_clicked', properties),
    planSelected: (properties) => track('landing_plan_selected', properties),
    navbarCTAClicked: (properties) => track('landing_navbar_cta_clicked', properties),
    capabilityClicked: (properties) => track('landing_capability_clicked', properties),
    eventClicked: (properties) => track('landing_event_clicked', properties),
    campaignCTAClicked: (properties) => track('landing_campaign_cta_clicked', properties),
  };
}

export function createBlogAnalytics(posthog: ReturnType<typeof usePostHog>) {
  const track = (eventName: string, properties?: Record<string, unknown>) => {
    if (!posthog) {
      console.warn('[Analytics] PostHog not initialized');
      return;
    }

    const enrichedProperties = {
      ...properties,
      timestamp: properties?.timestamp || new Date().toISOString(),
      product: 'blog',
    };

    posthog.capture(eventName, enrichedProperties);
  };

  return {
    featuresCTAClicked: (properties: BlogFeaturesCTAProperties) =>
      track('blog_features_cta_clicked', {
        ...properties,
        $set: {
          has_clicked_blog_cta: true,
          last_blog_cta_clicked_at: new Date().toISOString(),
        },
      }),
    postViewed: (properties: BlogPostViewProperties) =>
      track('blog_post_viewed', {
        ...properties,
        $set: {
          last_blog_post_viewed_at: new Date().toISOString(),
          last_blog_post_slug: properties.postSlug,
        },
      }),
    scrollDepthTracked: (properties: { postSlug: string; postTitle: string; depth: number }) =>
      track('blog_scroll_depth_tracked', properties),
    drawer: {
      shown: (properties: { postSlug: string; scrollDepth: number; device: 'mobile' | 'desktop' }) =>
        track('blog_drawer_shown', properties),
      closed: (properties: { postSlug: string; device: 'mobile' | 'desktop' }) =>
        track('blog_drawer_closed', properties),
      ctaClicked: (properties: { postSlug: string; device: 'mobile' | 'desktop' }) =>
        track('blog_drawer_cta_clicked', {
          ...properties,
          $set: {
            has_clicked_blog_cta: true,
            last_blog_cta_clicked_at: new Date().toISOString(),
          },
        }),
    },
  };
}
